// components/TestRunner/rules/Rule07_SOR_AntiCheat_Shared.js
import { RuleUtils } from "../../../utils/RuleUtils.js";

/**
 * Rule07_SOR_AntiCheat_Shared.js
 *
 * 🔧 Classe partagée pour les validations anti-triche nom vs métadonnées
 *
 * 🎯 Responsabilités :
 * • Validation croisée position : RuleUtils.extractPositionFromName() vs cableId + numeroFibreOrigine
 * • Validation croisée longueur d'onde : nom vs metadata.wavelength vs longueurOndeConfirmation
 * • Validation cohérence PBO : nom vs métadonnées internes (avec gestion troncature Viavi)
 * • Validation cohérence position physique : nom vs métadonnées internes (avec gestion troncature Viavi)
 * • Génération des messages d'erreur et warnings
 * • Détection anti-triche : fichiers renommés, erreurs configuration, manipulation données
 */

export class Rule07_SOR_AntiCheat_Shared {
  /**
   * Valider les fichiers SOR pour cohérence nom vs métadonnées
   * @param {Array} files - Tableau des fichiers à valider
   * @param {string} ruleCode - Code de la règle (R07b, R07c, etc.)
   * @returns {Array} Messages d'erreur et d'avertissement générés
   */
  static validateFiles(files, ruleCode) {
    // Analyser tous les fichiers
    const analysisResults = this._analyzeFiles(files);

    // Regrouper les erreurs par type
    const errorsByType = this._groupErrorsByType(analysisResults);

    // Générer les messages d'erreur et d'avertissement
    const messages = [];
    Object.entries(errorsByType).forEach(([errorType, fileResults]) => {
      if (fileResults.length > 0) {
        const errorInfo = this._getErrorInfo(errorType, fileResults);
        const messageLevel = this._getMessageLevel(errorType);

        messages.push({
          type: "error", // Type reste "error" pour compatibilité
          level: messageLevel, // Nouveau champ pour distinguer error/warning
          rule: ruleCode,
          title: errorInfo.title,
          message: errorInfo.message,
        });
      }
    });

    return messages;
  }

  /**
   * Analyser tous les fichiers .sor pour cohérence nom vs métadonnées
   * @param {Array} files - Tableau des fichiers
   * @returns {Array} Résultats d'analyse pour chaque fichier
   */
  static _analyzeFiles(files) {
    return files.map((file) => this._validateNameVsMetadata(file));
  }

  /**
   * Valider la cohérence entre nom de fichier et métadonnées
   * @param {Object} file - Objet fichier avec name et metadata
   * @returns {Object} Résultat de validation
   */
  static _validateNameVsMetadata(file) {
    const fileName = file.name || file.fileName || "";
    const metadata = file.metadata || {};

    const result = {
      fileName: fileName,
      errors: [],
      namePosition: null,
      metaPosition: null,
      nameWavelength: null,
      metaWavelength: null,
      confirmationWavelength: null,
    };

    // Tests existants (position et longueur d'onde avec RuleUtils)
    this._validateLegacyPosition(fileName, metadata, result);
    this._validateWavelength(fileName, metadata, result);

    // Nouveaux tests refactorisés (PBO et Position avec gestion Viavi)
    this._validatePboCoherence(fileName, metadata, result);
    this._validatePositionCoherence(fileName, metadata, result);

    return result;
  }

  /**
   * Valider la position avec la logique existante (RuleUtils)
   * @param {string} fileName - Nom du fichier
   * @param {Object} metadata - Métadonnées du fichier
   * @param {Object} result - Objet résultat à modifier
   */
  static _validateLegacyPosition(fileName, metadata, result) {
    const namePosition = RuleUtils.extractPositionFromName(fileName);
    const metaPosition = RuleUtils.extractPositionFromMetadata(metadata);

    result.namePosition = namePosition?.fullPosition;
    result.metaPosition = metaPosition?.fullPosition;

    if (namePosition && metaPosition) {
      if (namePosition.fullPosition !== metaPosition.fullPosition) {
        result.errors.push("position_incoherente");
      }
    }
  }

  /**
   * Valider la longueur d'onde
   * @param {string} fileName - Nom du fichier
   * @param {Object} metadata - Métadonnées du fichier
   * @param {Object} result - Objet résultat à modifier
   */
  static _validateWavelength(fileName, metadata, result) {
    const namePosition = RuleUtils.extractPositionFromName(fileName);

    if (namePosition) {
      const nameWave = namePosition.wavelength;
      const metaWave = metadata.wavelength;
      const confirmWave = metadata.longueurOndeConfirmation;

      result.nameWavelength = nameWave;
      result.metaWavelength = metaWave;
      result.confirmationWavelength = confirmWave;

      const matchesMeta = nameWave === metaWave;
      const matchesConfirm = confirmWave ? nameWave === confirmWave : true;

      if (!matchesMeta || !matchesConfirm) {
        result.errors.push("longueur_onde_incoherente");
      }
    }
  }

  /**
   * Valider la cohérence PBO entre nom de fichier et métadonnées
   * @param {string} fileName - Nom du fichier
   * @param {Object} metadata - Métadonnées du fichier
   * @param {Object} result - Objet résultat à modifier
   */
  static _validatePboCoherence(fileName, metadata, result) {
    const pboReference = this._extractPboFromFileName(fileName);
    if (!pboReference) {
      return; // Pas de PBO détectable dans le nom
    }

    const fields = ["cableId", "cableIdOrigine", "cableIdDestination"];
    const pboResults = {};

    fields.forEach((field) => {
      if (metadata[field]) {
        const fieldPbo = this._extractPboFromMetadata(metadata[field]);
        const validation = this._validateFieldCoherence(fieldPbo, pboReference);
        pboResults[field] = {
          value: fieldPbo,
          validation: validation,
        };
      }
    });

    // Déterminer le type d'erreur global
    const validations = Object.values(pboResults).map((r) => r.validation);
    const hasErrors = validations.some((v) => v.status === "error");
    const hasWarnings = validations.some((v) => v.status === "warning");

    if (hasErrors) {
      result.errors.push("pbo_incoherent");
    } else if (hasWarnings) {
      result.errors.push("pbo_tronque");
    }

    // Stocker les résultats pour les messages
    result.pboReference = pboReference;
    result.pboResults = pboResults;
  }

  /**
   * Valider la cohérence position physique entre nom de fichier et métadonnées
   * @param {string} fileName - Nom du fichier
   * @param {Object} metadata - Métadonnées du fichier
   * @param {Object} result - Objet résultat à modifier
   */
  static _validatePositionCoherence(fileName, metadata, result) {
    const positionReference = this._extractPositionFromFileName(fileName);
    if (!positionReference) {
      return; // Pas de position détectable dans le nom
    }

    const fields = ["cableId", "cableIdOrigine", "cableIdDestination"];
    const positionResults = {};

    fields.forEach((field) => {
      if (metadata[field]) {
        const fieldPosition = this._extractPositionFromMetadata(metadata[field]);
        const validation = this._validateFieldCoherence(fieldPosition, positionReference);
        positionResults[field] = {
          value: fieldPosition,
          validation: validation,
        };
      }
    });

    // Déterminer le type d'erreur global
    const validations = Object.values(positionResults).map((r) => r.validation);
    const hasErrors = validations.some((v) => v.status === "error");
    const hasWarnings = validations.some((v) => v.status === "warning");

    if (hasErrors) {
      result.errors.push("position_physique_incoherente");
    } else if (hasWarnings) {
      result.errors.push("position_physique_tronquee");
    }

    // Stocker les résultats pour les messages
    result.positionReference = positionReference;
    result.positionResults = positionResults;
  }

  /**
   * Extraire le PBO du nom de fichier
   * @param {string} fileName - Nom du fichier
   * @returns {string|null} PBO extrait ou null
   */
  static _extractPboFromFileName(fileName) {
    const match = fileName.match(/^(.+?)_B2-T/);
    return match ? match[1] : null;
  }

  /**
   * Extraire la position physique du nom de fichier
   * @param {string} fileName - Nom du fichier
   * @returns {string|null} Position extraite ou null
   */
  static _extractPositionFromFileName(fileName) {
    const match = fileName.match(/_B2-T(\d+)-M([A-Z]+)-(\d+)/);
    return match ? `B2-T${match[1]}-M${match[2]}-${match[3]}` : null;
  }

  /**
   * Extraire le PBO d'une métadonnée
   * @param {string} metadataValue - Valeur de métadonnée
   * @returns {string|null} PBO extrait ou null
   */
  static _extractPboFromMetadata(metadataValue) {
    const match = metadataValue.match(/^(.+?)_B2-T/);
    return match ? match[1] : metadataValue; // Si pas de pattern, retourner la valeur complète
  }

  /**
   * Extraire la position physique d'une métadonnée
   * @param {string} metadataValue - Valeur de métadonnée
   * @returns {string|null} Position extraite ou null
   */
  static _extractPositionFromMetadata(metadataValue) {
    const match = metadataValue.match(/B2-T(\d+)(?:-M([A-Z]+))?(?:-(\d+))?/);
    if (!match) return null;

    let position = `B2-T${match[1]}`;
    if (match[2]) position += `-M${match[2]}`;
    if (match[3]) position += `-${match[3]}`;

    return position;
  }

  /**
   * Valider la cohérence entre une valeur de champ et une référence avec support PBO et patterns SC
   * @param {string} fieldValue - Valeur du champ
   * @param {string} reference - Valeur de référence
   * @returns {Object} Résultat de validation
   */
  static _validateFieldCoherence(fieldValue, reference) {
    if (!fieldValue || !reference) {
      return { status: "error", type: "missing" };
    }

    // 1. Test égalité stricte
    if (fieldValue === reference) {
      return { status: "success", type: "equal" };
    }

    // 2. Test sous-chaîne (bienveillant - dans les deux sens)
    if (reference.includes(fieldValue) || fieldValue.includes(reference)) {
      return { status: "warning", type: "substring" };
    }

    // 3. Test cohérence numéros PBO
    function extractPboNumber(value) {
      const match = value.match(/PBO([0-9]{2,4}[A-Z]?)(?:_|$)/i);
      if (match) {
        const num = match[1];
        // Normaliser vers 3 digits si c'est un nombre pur
        if (/^\d+$/.test(num)) {
          return parseInt(num, 10).toString().padStart(3, "0");
        }
        // Si c'est nombre + lettres, normaliser la partie numérique
        const numMatch = num.match(/^(\d+)([A-Z]+)$/);
        if (numMatch) {
          return parseInt(numMatch[1], 10).toString().padStart(3, "0") + numMatch[2];
        }
        return num;
      }
      return null;
    }

    const refNum = extractPboNumber(reference);
    const fieldNum = extractPboNumber(fieldValue);

    if (refNum && fieldNum && refNum === fieldNum) {
      return { status: "success", type: "pbo_number_match" };
    }

    // 4. Test cohérence patterns SC (nouveau)
    function extractScPattern(value) {
      const match = value.match(/(?:BE-)?SRO-BPI-(\d+)[-_]SC-(\d+)-(\d+)/i);
      if (match) {
        return {
          sroNumber: match[1],
          scNumber: match[2],
          finalNumber: match[3],
        };
      }
      return null;
    }

    const refSc = extractScPattern(reference);
    const fieldSc = extractScPattern(fieldValue);

    if (refSc && fieldSc) {
      const sroMatch = refSc.sroNumber === fieldSc.sroNumber;
      const scMatch = refSc.scNumber === fieldSc.scNumber;
      const finalMatch = refSc.finalNumber === fieldSc.finalNumber;

      if (sroMatch && scMatch && finalMatch) {
        return { status: "success", type: "sc_pattern_match" };
      }
    }

    // 5. Aucune correspondance
    return { status: "error", type: "incoherent" };
  }

  /**
   * Déterminer le niveau du message (error ou warning)
   * @param {string} errorType - Type d'erreur
   * @returns {string} "error" ou "warning"
   */
  static _getMessageLevel(errorType) {
    const warningTypes = ["pbo_tronque", "position_physique_tronquee"];
    return warningTypes.includes(errorType) ? "warning" : "error";
  }

  /**
   * Regrouper les erreurs par type
   * @param {Array} analysisResults - Résultats d'analyse
   * @returns {Object} Erreurs regroupées par type
   */
  static _groupErrorsByType(analysisResults) {
    const errorsByType = {
      position_incoherente: [],
      longueur_onde_incoherente: [],
      pbo_incoherent: [],
      pbo_tronque: [],
      position_physique_incoherente: [],
      position_physique_tronquee: [],
    };

    analysisResults.forEach((result) => {
      result.errors.forEach((errorType) => {
        if (errorsByType[errorType]) {
          errorsByType[errorType].push(result);
        }
      });
    });

    return errorsByType;
  }

  /**
   * Obtenir les informations d'erreur pour un type donné
   * @param {string} errorType - Type d'erreur
   * @param {Array} files - Fichiers avec cette erreur
   * @returns {Object} Informations d'erreur
   */
  static _getErrorInfo(errorType, files) {
    switch (errorType) {
      case "position_incoherente":
        return {
          title: "Position incohérente entre nommage externe du fichier et nommage interne",
          message: this._generatePositionMessage(files),
        };
      case "longueur_onde_incoherente":
        return {
          title: "Longueur d'onde incohérente entre nommage externe du fichier et nommage interne",
          message: this._generateWavelengthMessage(files),
        };
      case "pbo_incoherent":
        return {
          title: "PBO incohérent entre nommage externe du fichier et nommage interne",
          message: this._generatePboErrorMessage(files),
        };
      case "pbo_tronque":
        return {
          title: "PBO tronqué dans les métadonnées internes (limitation équipement)",
          message: this._generatePboWarningMessage(files),
        };
      case "position_physique_incoherente":
        return {
          title: "Position physique incohérente entre nommage externe du fichier et nommage interne",
          message: this._generatePositionErrorMessage(files),
        };
      case "position_physique_tronquee":
        return {
          title: "Position physique tronquée dans les métadonnées internes (limitation équipement)",
          message: this._generatePositionWarningMessage(files),
        };
      default:
        return {
          title: "Incohérence détectée",
          message: "Erreur inconnue détectée",
        };
    }
  }

  /**
   * Générer le message pour erreurs de PBO
   */
  static _generatePboErrorMessage(files) {
    let message = `<strong>⚠️ Problème détecté :</strong><br>`;
    message += `PBO incohérent entre nommage externe du fichier et nommage interne<br><br>`;

    message += `<strong>Détail du problème :</strong><br>`;
    files.forEach((file) => {
      message += `<strong>${file.fileName}</strong><br>`;
      message += `- PBO dans le nom du fichier : ${file.pboReference || "Non détecté"}<br>`;

      if (file.pboResults) {
        Object.entries(file.pboResults).forEach(([field, result]) => {
          const status = result.validation.status === "error" ? "❌" : "⚠️";
          message += `- PBO dans ${field} : ${result.value || "Non détecté"} ${status}<br>`;
        });
      }
      message += `<br>`;
    });

    message += `<strong>⚡ Action requise :</strong><br>`;
    message += `- Vérifier la cohérence du PBO dans le nom du fichier et dans les métadonnées internes`;

    return message;
  }

  /**
   * Générer le message pour warnings de PBO
   */
  static _generatePboWarningMessage(files) {
    let message = `<strong>⚠️ Avertissement :</strong><br>`;
    message += `PBO tronqué dans les métadonnées internes (limitation équipement)<br><br>`;

    message += `<strong>Détail :</strong><br>`;
    files.forEach((file) => {
      message += `<strong>${file.fileName}</strong><br>`;
      message += `- PBO complet dans le nom : ${file.pboReference || "Non détecté"}<br>`;

      if (file.pboResults) {
        Object.entries(file.pboResults).forEach(([field, result]) => {
          if (result.validation.status === "warning") {
            message += `- PBO dans ${field} : ${result.value || "Non détecté"} (tronqué)<br>`;
          }
        });
      }
      message += `<br>`;
    });

    message += `<strong>ℹ️ Information :</strong><br>`;
    message += `- Cette troncature est normale due aux limitations du réflectomètre Viavi`;

    return message;
  }

  /**
   * Générer le message pour erreurs de position physique
   */
  static _generatePositionErrorMessage(files) {
    let message = `<strong>⚠️ Problème détecté :</strong><br>`;
    message += `Position physique incohérente entre nommage externe du fichier et nommage interne<br><br>`;

    message += `<strong>Détail du problème :</strong><br>`;
    files.forEach((file) => {
      message += `<strong>${file.fileName}</strong><br>`;
      message += `- Position dans le nom du fichier : ${file.positionReference || "Non détectée"}<br>`;

      if (file.positionResults) {
        Object.entries(file.positionResults).forEach(([field, result]) => {
          const status = result.validation.status === "error" ? "❌" : "⚠️";
          message += `- Position dans ${field} : ${result.value || "Non détectée"} ${status}<br>`;
        });
      }
      message += `<br>`;
    });

    message += `<strong>⚡ Action requise :</strong><br>`;
    message += `- Vérifier la cohérence de la position physique dans le nom du fichier et dans les métadonnées internes`;

    return message;
  }

  /**
   * Générer le message pour warnings de position physique
   */
  static _generatePositionWarningMessage(files) {
    let message = `<strong>⚠️ Avertissement :</strong><br>`;
    message += `Position physique tronquée dans les métadonnées internes (limitation équipement)<br><br>`;

    message += `<strong>Détail :</strong><br>`;
    files.forEach((file) => {
      message += `<strong>${file.fileName}</strong><br>`;
      message += `- Position complète dans le nom : ${file.positionReference || "Non détectée"}<br>`;

      if (file.positionResults) {
        Object.entries(file.positionResults).forEach(([field, result]) => {
          if (result.validation.status === "warning") {
            message += `- Position dans ${field} : ${result.value || "Non détectée"} (tronqué)<br>`;
          }
        });
      }
      message += `<br>`;
    });

    message += `<strong>ℹ️ Information :</strong><br>`;
    message += `- Cette troncature est normale due aux limitations du réflectomètre Viavi`;

    return message;
  }

  /**
   * Générer le message pour erreurs de position (ancienne méthode conservée)
   */
  static _generatePositionMessage(files) {
    let message = `<strong>⚠️ Problème détecté :</strong><br>`;
    message += `Position incohérente entre nommage externe du fichier et nommage interne<br><br>`;

    message += `<strong>Détail du problème :</strong><br>`;
    files.forEach((file) => {
      message += `<strong>${file.fileName}</strong><br>`;
      message += `- Position dans le nom du fichier : ${file.namePosition || "Non détectée"}<br>`;
      message += `- Position dans les métadonnées : ${file.metaPosition || "Non détectée"}<br>`;
      message += `- Correspondance nom/métadonnées : ❌<br><br>`;
    });

    message += `<strong>⚡ Action requise :</strong><br>`;
    message += `- Vérifier la cohérence entre nom du fichier et métadonnées internes`;

    return message;
  }

  /**
   * Générer le message pour erreurs de longueur d'onde
   */
  static _generateWavelengthMessage(files) {
    let message = `<strong>⚠️ Problème détecté :</strong><br>`;
    message += `Longueur d'onde incohérente entre nommage externe du fichier et nommage interne<br><br>`;

    message += `<strong>Détail du problème :</strong><br>`;
    files.forEach((file) => {
      message += `<strong>${file.fileName}</strong><br>`;
      message += `- Longueur d'onde dans le nom du fichier : ${file.nameWavelength || "Non détectée"} nm<br>`;
      const metaInfo = file.confirmationWavelength
        ? `${file.metaWavelength || "Non détectée"} nm / ${file.confirmationWavelength} nm`
        : `${file.metaWavelength || "Non détectée"} nm`;
      message += `- Longueur d'onde dans les métadonnées : ${metaInfo}<br>`;
      message += `- Correspondance nom/métadonnées : ❌<br><br>`;
    });

    message += `<strong>⚡ Action requise :</strong><br>`;
    message += `- Vérifier la cohérence de la longueur d'onde dans le nom du fichier et dans les métadonnées internes`;

    return message;
  }
}
